import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateTenantDocument,
  type ActivateTenantMutation,
  type ActivateTenantMutationVariables,
  DeactivateTenantDocument,
  type DeactivateTenantMutation,
  type DeactivateTenantMutationVariables,
  GetTenantDocument,
  GetTenantsDocument,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate a tenant. Returns plain submitters plus a `processing`
 * flag; the calling component owns the confirm dialog.
 */
export function useTenantActivation() {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [activate] = useMutation<ActivateTenantMutation, ActivateTenantMutationVariables>(ActivateTenantDocument, {
    refetchQueries: [GetTenantsDocument, GetTenantDocument],
    awaitRefetchQueries: true,
  });
  const [deactivate] = useMutation<DeactivateTenantMutation, DeactivateTenantMutationVariables>(
    DeactivateTenantDocument,
    { refetchQueries: [GetTenantsDocument, GetTenantDocument], awaitRefetchQueries: true },
  );

  const onActivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        await activate({ variables: { id } });
        toast.success(t("feature.common.activatedSuccessfully", { model: "tenant" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activate, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        await deactivate({ variables: { id } });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "tenant" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivate, t],
  );

  return { onActivate, onDeactivate, processing };
}
